import { useState } from 'react'
import {
  PANAS_ITEMS,
  SCALE_LABELS,
  SCORE_WORDS,
  calculatePanasPercent,
  panasScoreFromPartialAnswers,
} from '@/utils/panas'
import styles from './PANASCheck.module.css'

const FLIP_MS = 420
const EXIT_FADE_MS = 600

export interface PANASCheckProps {
  onComplete: (score: number) => void
  /** Called when skipped with no answers — bypasses score display */
  onSkip: () => void
  moodCheckLabel?: string
}

export default function PANASCheck({
  onComplete,
  onSkip,
  moodCheckLabel = 'Mood check',
}: PANASCheckProps) {
  const [frontWord,     setFrontWord]     = useState(PANAS_ITEMS[0].word)
  const [frontType,     setFrontType]     = useState(PANAS_ITEMS[0].type)
  const [selectedScore, setSelectedScore] = useState<number | null>(null)
  const [isFlipped,     setIsFlipped]     = useState(false)
  const [navKey,        setNavKey]        = useState(0)
  const [navDir,        setNavDir]        = useState<'init' | 'fwd' | 'back'>('init')
  const [busy,          setBusy]          = useState(false)
  const [hoveredValue,  setHoveredValue]  = useState<number | null>(null)
  const [index,         setIndex]         = useState(0)
  const [answers,       setAnswers]       = useState<Record<number, number>>({})
  const [exiting,       setExiting]       = useState(false)

  const goTo = (newIdx: number, dir: 'fwd' | 'back') => {
    if (busy || newIdx < 0 || newIdx >= 20) return
    setBusy(true)
    setFrontWord(PANAS_ITEMS[newIdx].word)
    setFrontType(PANAS_ITEMS[newIdx].type)
    setIndex(newIdx)
    setSelectedScore(answers[newIdx] ?? null)

    if (isFlipped) {
      setIsFlipped(false)
      setTimeout(() => setBusy(false), FLIP_MS + 40)
    } else {
      setNavDir(dir)
      setNavKey(k => k + 1)
      setTimeout(() => setBusy(false), 300)
    }
  }

  const handleAnswer = (value: number) => {
    if (busy) return
    setAnswers(prev => ({ ...prev, [index]: value }))
    setSelectedScore(value)
    if (!isFlipped) {
      setBusy(true)
      setIsFlipped(true)
      setTimeout(() => setBusy(false), FLIP_MS + 40)
    }
  }

  const finalize = (all: Record<number, number>) => {
    completeWithScore(calculatePanasPercent(all))
  }

  const completeWithScore = (pct: number) => {
    setExiting(true)
    setTimeout(() => onComplete(pct), EXIT_FADE_MS)
  }

  const skipWithoutScore = () => {
    setExiting(true)
    setTimeout(() => onSkip(), EXIT_FADE_MS)
  }

  const handleSkipToday = () => {
    const partial = panasScoreFromPartialAnswers(answers)
    if (partial !== null) completeWithScore(partial)
    else skipWithoutScore()
  }

  const handleFwd = () => {
    if (!canFwd) return
    if (index === 19) finalize({ ...answers })
    else goTo(index + 1, 'fwd')
  }

  const handleAreaEnter = () => {
    if (!busy && selectedScore !== null && !isFlipped) setIsFlipped(true)
  }
  const handleAreaLeave = () => {
    if (!busy && selectedScore !== null && isFlipped) setIsFlipped(false)
  }
  const handleCardToggle = () => {
    if (!busy && selectedScore !== null) setIsFlipped(f => !f)
  }

  const currentAnswer = answers[index]
  const canFwd  = currentAnswer !== undefined && !busy
  const canBack = index > 0 && !busy

  const descriptorText =
    hoveredValue  !== null        ? SCALE_LABELS[hoveredValue]
    : currentAnswer !== undefined ? SCALE_LABELS[currentAnswer]
    : ''

  return (
    <div className={`${styles.panas} ${exiting ? styles.panasExiting : ''}`}>
      <div className={styles.panasHeader}>
        <p className={styles.panasHeaderLabel}>{moodCheckLabel}</p>
        <p className={styles.panasProgress} aria-label={`Card ${index + 1} of 20`}>
          {index + 1} <span className={styles.panasProgressOf}>/ 20</span>
        </p>
      </div>

      <div className={styles.progressTrack} aria-hidden="true">
        <div
          className={styles.progressFill}
          style={{ width: `${((index + 1) / 20) * 100}%` }}
        />
      </div>

      <div
        className={styles.cardStage}
        onClick={handleCardToggle}
        onMouseEnter={handleAreaEnter}
        onMouseLeave={handleAreaLeave}
        style={{ cursor: selectedScore !== null ? 'pointer' : 'default' }}
        aria-label={selectedScore !== null ? 'Tap to flip card' : undefined}
        role={selectedScore !== null ? 'button' : undefined}
      >
        <div
          key={navKey}
          data-dir={navDir}
          data-flipped={isFlipped ? 'true' : 'false'}
          className={styles.cardContainer}
        >
          <div
            className={styles.cardFront}
            data-type={frontType}
            aria-live="polite"
            aria-atomic="true"
          >
            {frontWord}
          </div>
          <div className={styles.cardBack} data-type={frontType} aria-hidden="true">
            {selectedScore !== null && (
              <>
                <span className={styles.backWord}>{SCORE_WORDS[selectedScore]}</span>
                <span className={styles.backLabel}>{SCALE_LABELS[selectedScore].toUpperCase()}</span>
              </>
            )}
          </div>
        </div>
      </div>

      <div className={styles.interactionArea}>
        <div
          className={styles.responseRow}
          role="group"
          aria-label={`How much do you feel ${frontWord.toLowerCase()} right now?`}
        >
          {([1, 2, 3, 4, 5] as const).map(v => (
            <button
              key={v}
              type="button"
              className={styles.responseBtn}
              data-selected={currentAnswer === v}
              onClick={() => handleAnswer(v)}
              disabled={busy}
              aria-label={SCALE_LABELS[v]}
              aria-pressed={currentAnswer === v}
              onMouseEnter={() => setHoveredValue(v)}
              onMouseLeave={() => setHoveredValue(null)}
              onFocus={()    => setHoveredValue(v)}
              onBlur={()     => setHoveredValue(null)}
            >
              {v}
            </button>
          ))}
        </div>

        <div className={styles.descriptorBox} aria-live="polite" aria-atomic="true">
          <span className={styles.descriptorText}>{descriptorText}</span>
        </div>

        <div className={styles.navRow}>
          <button
            type="button"
            className={styles.navBtn}
            onClick={() => goTo(index - 1, 'back')}
            disabled={!canBack}
            aria-label="Previous word"
          >
            ←
          </button>
          <button
            type="button"
            className={styles.navBtn}
            onClick={handleFwd}
            disabled={!canFwd}
            aria-label={index === 19 ? 'See your score' : 'Next word'}
          >
            →
          </button>
        </div>
      </div>

      <button
        type="button"
        className={styles.skipTodayLink}
        onClick={handleSkipToday}
      >
        Skip this today
      </button>
    </div>
  )
}
